// 員工文件庫 (V273) — 勞動契約/offer/證照。清單 + 直傳 Blob 上傳 + 下載 + 刪除。
// 上傳同簽核附件:upload-token → PUT Blob → POST metadata。hr/系統管理員用。
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useActor } from '../lib/actor-store.ts';
import { type ApiError, api } from '../lib/api.ts';
import { useConfirm } from '../lib/confirm.tsx';
import { formatBytes } from '../lib/format.ts';
import { DOC_TYPE_LABELS, useMemberDocuments } from '../lib/use-employee-profile.ts';
import { EmptyState } from './EmptyState.tsx';
import { RowMenu } from './RowMenu.tsx';
import { inputClassCompact } from './form/Field.tsx';
import { FileDropZone } from './form/FileDropZone.tsx';

interface UploadToken {
  sas_url: string;
  blob_url: string;
  max_size_bytes: number;
}

// 對齊後端 employee-documents.ts 的 upload-token 上限。
const MAX_DOC_BYTES = 25 * 1024 * 1024;

export function EmployeeDocuments({ userId }: { userId: number }) {
  const actor = useActor((s) => s.actor);
  const qc = useQueryClient();
  const q = useMemberDocuments(actor, userId);
  const confirm = useConfirm();
  const [docType, setDocType] = useState('labor_contract');
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // 上方「合約 缺 / ✓」徽章讀的是成員名冊 (['users']) 的 has_labor_contract,
  // 不是這份文件清單 —— 只刷新清單的話, 上傳完徽章仍停在「缺」, 要重新整理才變。
  const refresh = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ['member-documents', userId, actor] }),
      qc.invalidateQueries({ queryKey: ['users'] }),
    ]);

  async function onPick(file: File | null) {
    if (!file) return;
    setErr(null);
    setBusy(`上傳中 ${file.name}…`);
    try {
      const token = await api<UploadToken>(`/api/v1/members/${userId}/documents/upload-token`, {
        actor,
        method: 'POST',
        json: { filename: file.name, mime_type: file.type || undefined, size_bytes: file.size },
      });
      const put = await fetch(token.sas_url, {
        method: 'PUT',
        headers: {
          'x-ms-blob-type': 'BlockBlob',
          'Content-Type': file.type || 'application/octet-stream',
        },
        body: file,
      });
      if (!put.ok) throw new Error(`Blob 上傳失敗 (${put.status})`);
      await api(`/api/v1/members/${userId}/documents`, {
        actor,
        method: 'POST',
        json: {
          doc_type: docType,
          filename: file.name,
          blob_url: token.blob_url,
          content_type: file.type || undefined,
          size_bytes: file.size,
        },
      });
      await refresh();
    } catch (e) {
      setErr((e as ApiError).message ?? String(e));
    } finally {
      setBusy(null);
    }
  }

  async function onDelete(id: string) {
    setErr(null);
    try {
      await api(`/api/v1/members/${userId}/documents/${id}`, { actor, method: 'DELETE' });
      await refresh();
    } catch (e) {
      setErr((e as ApiError).message ?? String(e));
    }
  }

  const items = q.data ?? [];
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <div className="flex items-center gap-2 flex-wrap mb-2">
        <h3 className="text-sm font-medium text-slate-700 flex-1">
          文件庫（勞動契約 / 錄取通知 / 證照）
        </h3>
        <select
          value={docType}
          onChange={(e) => setDocType(e.target.value)}
          className={inputClassCompact}
        >
          {Object.entries(DOC_TYPE_LABELS).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
      </div>
      {/* 2026-08-08: 原本只有一顆按鈕、不能拖、也沒說上限。 換成共用 FileDropZone。 */}
      <div className="mb-2">
        <FileDropZone
          onFiles={(fs) => onPick(fs[0] ?? null)}
          maxBytes={MAX_DOC_BYTES}
          disabled={!!busy}
        />
      </div>
      {err && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2 rounded mb-2">
          {err}
        </div>
      )}
      {items.length === 0 && !busy ? (
        <EmptyState>尚無文件。選類別後上傳。</EmptyState>
      ) : (
        <ul className="divide-y divide-slate-100">
          {items.map((d) => (
            <li key={d.id} className="flex items-center gap-2 py-2 text-sm">
              {/* 2026-07-20: 補上 ring — §6.3 chip 三件組是 -50 底 / -700 字 / -200 ring。 */}
              <span className="text-[10px] shrink-0 px-1.5 py-0.5 rounded-full ring-1 bg-slate-50 text-slate-600 ring-slate-200">
                {DOC_TYPE_LABELS[d.doc_type] ?? d.doc_type}
              </span>
              <a
                href={d.download_url}
                target="_blank"
                rel="noreferrer"
                className="text-slate-700 hover:text-slate-900 hover:underline truncate flex-1 min-w-0"
                title={d.filename}
              >
                📎 {d.filename}
              </a>
              <span className="text-xs text-slate-400 shrink-0 tabular-nums">
                {formatBytes(d.size_bytes)}
              </span>
              {/* 文件沒有編輯表單 → 刪除收進 ⋯ 溢位選單 (DESIGN_GUIDE §4.9)。 */}
              <div className="shrink-0">
                <RowMenu
                  label={d.filename}
                  items={[
                    {
                      label: '刪除',
                      danger: true,
                      // 2026-08-06: 原本按下去就直接刪，沒有任何確認 —— 這些是勞動契約 /
                      // offer letter 等人事文件，誤刪代價高（DESIGN_GUIDE §4.10）。
                      onClick: async () => {
                        if (
                          !(await confirm({
                            message: `刪除文件「${d.filename}」？`,
                            detail: '此動作無法復原。',
                            danger: true,
                            confirmLabel: '刪除',
                          }))
                        )
                          return;
                        onDelete(d.id);
                      },
                    },
                  ]}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
